<template>
  <div class="card">
    <h2 class="card-header">Request a Comprehensive Document Upload</h2>
    <div class="card-body">
      <div class="form-grid selectors">
        <div class="form-group">
          <label for="project">Project</label>
          <select
            id="project"
            v-model="selectedProject"
            class="form-control"
            required
          >
            <option v-for="project in projects" :key="project.id" :value="project.id">
              {{ project.name }}
            </option>
          </select>
        </div>
        <div class="form-group">
          <label for="subject">Compliance Subject</label>
          <select
            id="subject"
            v-model="selectedSubjectId"
            class="form-control"
            :disabled="!subjects.length"
            required
          >
            <option v-if="!subjects.length" value="">No subjects in this project</option>
            <option v-for="subject in subjects" :key="subject.id" :value="subject.id">
              {{ subject.subject_entity_name }}
            </option>
          </select>
        </div>
      </div>

      <div v-if="loading" class="loading-row">
        <i class="fas fa-spinner fa-spin"></i> Loading project data…
      </div>

      <div v-else-if="selectedSubjectContext" class="request-form-wrap">
        <UploadRequestForm
          :project-id="selectedProject"
          :document-types="[]"
          :workflows="[]"
          :subject-context="selectedSubjectContext"
          @request-sent="handleRequestSent"
        />
      </div>

      <div v-else-if="selectedProject && !subjects.length" class="empty-hint">
        Add a compliance subject to this project before sending an upload request.
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import UploadRequestForm from '@/components/UploadRequestForm.vue';

export default {
  name: 'ComprehensiveRequestDashboard',
  components: {
    UploadRequestForm,
  },
  data() {
    return {
      projects: [],
      subjects: [],
      selectedProject: '',
      selectedSubjectId: '',
      loading: false,
    };
  },
  computed: {
    selectedSubjectContext() {
      if (!this.selectedSubjectId) return null;
      return this.subjects.find((s) => s.id === this.selectedSubjectId) || null;
    },
  },
  async created() {
    await this.fetchProjects();
    if (this.selectedProject) {
      await this.fetchProjectSubjects(this.selectedProject);
    }
  },
  methods: {
    async fetchProjects() {
      this.loading = true;
      try {
        const response = await api.get('/projects/');
        this.projects = response.data?.projects || response.data?.results || response.data || [];
        if (this.projects.length > 0) {
          this.selectedProject = this.projects[0].id;
        }
      } catch (error) {
        console.error('Failed to load projects:', error);
      } finally {
        this.loading = false;
      }
    },
    async fetchProjectSubjects(projectId) {
      if (!projectId) {
        this.subjects = [];
        this.selectedSubjectId = '';
        return;
      }
      this.loading = true;
      try {
        const response = await api.get('/compliance-subjects/', {
          params: { project: projectId },
        });
        this.subjects = response.data?.results || response.data || [];
        this.selectedSubjectId = this.subjects[0]?.id || '';
      } catch (error) {
        console.error('Failed to load subjects:', error);
        this.subjects = [];
        this.selectedSubjectId = '';
      } finally {
        this.loading = false;
      }
    },
    handleRequestSent(result) {
      if (result?.success) {
        // UploadRequestForm resets itself on success
        return;
      }
      if (result?.message) {
        console.error('Upload request failed:', result.message);
      }
    },
  },
  watch: {
    selectedProject(newProjectId) {
      this.fetchProjectSubjects(newProjectId);
    },
  },
};
</script>

<style scoped>
.card-header {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  font-size: 1.1rem;
  font-weight: 600;
}
.card-body {
  padding: 1.5rem;
}
.form-grid.selectors {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}
.form-group {
  margin-bottom: 0;
}
label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
}
.loading-row,
.empty-hint {
  color: #64748b;
  padding: 1rem 0;
}
.request-form-wrap {
  border-top: 1px solid #e2e8f0;
  padding-top: 1rem;
}
</style>
